/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { IonIcon } from '@ionic/react';
import { chevronDown, chevronForward, close, closeCircle, search } from 'ionicons/icons';
import { chain, map, noop } from 'lodash';
import { InputAdornment, TextField } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';

import useWhiteLabelStyles, { WHITE_LABEL_TYPE_ENUM } from 'hooks/useWhiteLabelStyles';
import SmartLoading from 'components/SmartLoading';
import { getCleanGameName, isTextFinite } from 'common/common';
import useSlotDetailHistory from 'globalServices/useSlotDetailHistory';
import { ISearchInfo } from 'appRedux/models/casinoModels';
import { SlotReduxCommandCreator } from 'appRedux/actions/slotCommandCreator';

import { useRefWithClickOutsideListenerCallback } from '../../common/reactHooks';
import { titleCase } from '../../common/include';
import { IState } from 'appRedux/createStore';
import { casinoIdListEnum } from 'common/commonEnum';
import { SlotCompareReduxCommandCreator } from 'appRedux/actions/slotCompareCommandCreator';
import {
  isInsightCardRow,
  searchRowDetailId,
  searchSelectionDetailId,
} from 'services/insightCasino';
import {
  buildInsightSearchSections,
  flattenInsightSearchSlotRows,
  InsightSearchGroup,
  InsightSearchSection,
} from 'lib/insightSearchGroups';
import { isInsightSearchCasino } from 'lib/insightSearchGate';
import { applyBankFilter } from 'lib/applyBankFilter';
import { dedupeSearchRows } from 'lib/searchRowDedupe';

interface Slot {
  game_name: string;
  slot_number: string;
  denom: string;
}

interface SubGame {
  game_name: string;
  slot_number: number;
  slots?: Slot[];
  denom: string;
  type: string;
}

interface GameInfo {
  game_name: string;
  slot_number: string;
  total: number;
  field: string;
  sub_game_names: SubGame[];
  /** Insight: keep manufacturer / Bank Location header casing. */
  preserveHeaderCasing?: boolean;
}

interface SearchProps {
  customCls?: string;
  shouldClearSearch?: boolean;
  shouldFocus?: boolean;
  cssClass?: string;
  closeHandler: () => void;
  onClickItem?: (slotNumbers: number[]) => void;
  searchData?: ISearchInfo[];
  searchLoading?: boolean;
  autocomplete?: string;
}
export type ISearchKeyword = 'lookup' | 'theme' | 'style';
export type ISearchKeywordValues = ISearchKeyword[];

type ResultType = {
  [key: string]: any;
};

const Search = ({
  customCls = '',
  shouldClearSearch,
  cssClass = '',
  closeHandler,
  onClickItem = noop,
  searchData,
  searchLoading,
  autocomplete = 'off',
}: SearchProps): ReactElement<any, any> | null => {
  const { addToSlotHistory } = useSlotDetailHistory();
  const dispatch = useDispatch();
  const slotCommands = SlotReduxCommandCreator(dispatch);
  const slotCompareCommands = SlotCompareReduxCommandCreator(dispatch);

  const casinoId = useSelector<IState>(
    (state) => state.app.persistedState.casinoSchema?.kpCasinoPk
  ) as number;

  const insightInstance = useSelector<IState>(
    (state) => state.app.persistedState.casinoSchema?.insightInstance
  ) as number | undefined;

  // Strict === 1 only. Other insightInstance values keep today's search UX.
  const isInsightCasino = isInsightSearchCasino(insightInstance);

  const [gameName, setGameName] = useState<any>(null);
  const [gameArr, setGameArr] = useState<any>();
  const [filterArr, setFilterArr] = useState<any>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  /** Raw insight search rows (GetCasinoInsights / machines join) for grouping. */
  const [insightMachines, setInsightMachines] = useState<ISearchInfo[]>([]);
  /** Insight Slot Search: Floor / Manufacturer / Game Name collapsible sections. */
  const [insightSections, setInsightSections] = useState<InsightSearchSection[]>([]);
  /** Override defaultOpen per section id; cleared when the query changes. */
  const [insightSectionOpen, setInsightSectionOpen] = useState<Record<string, boolean>>({});
  /** Floor / Manufacturer group rows: expanded keys; cleared when the query changes. */
  const [insightGroupOpen, setInsightGroupOpen] = useState<Record<string, boolean>>({});

  const btnstyles = useWhiteLabelStyles(WHITE_LABEL_TYPE_ENUM.BUTTON);
  const closeDropdown = useCallback(() => {}, []);
  const gameInputRefDiv = useRefWithClickOutsideListenerCallback(closeDropdown);

  useEffect(() => {
    if (shouldClearSearch) {
      setSearchValue('');
      setGameName(null);
      clearSearch();
    }
  }, [shouldClearSearch]);

  const processData = (field: string, originalData: ISearchInfo[]) => {
    return chain(originalData)
      .filter((object: ResultType) => object[field] != null && object[field] !== '')
      .groupBy(field)
      .map((groupedData, key) => {
        let gameData;
        const subGamedata = map(groupedData, (data) => {
          return {
            type: field,
            game_name: getCleanGameName(data.game_name)?.replaceAll('/', ' '),
            slot_number: Number(data.slot_number),
            denom: data.denom,
          };
        });

        if (field !== 'game_name') {
          const data = chain(subGamedata)
            .groupBy('game_name')
            .map((value: SubGame[], subKey: string) => {
              return {
                game_name: getCleanGameName(subKey).replaceAll('/', ' '),
                slots: value.map((slot: SubGame) => ({
                  ...slot,
                  slot_number: slot.slot_number,
                })),
                type: field,
                denom: value[0]?.denom,
              };
            })
            .value();
          gameData = data;
        } else {
          gameData = subGamedata;
        }

        return {
          game_name: getCleanGameName(key).replaceAll('/', ' '),
          total: subGamedata.length,
          sub_game_names: gameData,
        };
      })
      .value();
  };

  const processDataOtherCasinos = (field: string, originalData: ISearchInfo[]) => {
    return chain(originalData)
      .filter((object: ResultType) => object[field] != null && object[field] !== '')
      .groupBy('game_name')
      .map((groupedData, key) => {
        const subGamedata: SubGame[] = map(groupedData, (data) => {
          const gameName: string = (data as Record<string, any>)[field];
          return {
            type: field,
            game_name: gameName.trim().replace(/\s+/g, ' ').toUpperCase(),
            slot_number: Number(data.slot_number),
            denom: data.denom,
          };
        });

        let gameData;

        if (field !== 'game_name') {
          const data = chain(subGamedata)
            .groupBy('game_name')
            .map((value: SubGame[], subKey: string) => {
              return {
                game_name: subKey.trim().replace(/\s+/g, ' ').toUpperCase(),
                slots: value.map((slot: SubGame) => ({
                  ...slot,
                  slot_number: slot.slot_number,
                })),
                type: field,
                denom: value[0]?.denom,
              };
            })
            .value();
          gameData = data;
        } else {
          gameData = subGamedata;
        }

        return {
          game_name: getCleanGameName(key).replaceAll('/', ' '),
          total: subGamedata.length,
          sub_game_names: gameData,
        };
      })
      .value();
  };

  const processDataOtherCasinosGame = (field: string, originalData: ISearchInfo[]) => {
    return chain(originalData)
      // .filter((object: ResultType) => object['theme'] === null || object['theme'] === '')
      .groupBy('game_name')
      .map((groupedData, key) => {
        const subGamedata: SubGame[] = map(groupedData, (data) => {
          return {
            type: field,
            game_name: getCleanGameName((data as Record<string, any>)[field])?.replaceAll('/', ' '),
            slot_number: Number(data.slot_number),
            denom: data.denom,
          };
        });

        return {
          game_name: getCleanGameName(key).replaceAll('/', ' '),
          total: subGamedata.length,
          sub_game_names: subGamedata,
        };
      })
      .value();
  };

  useEffect(() => {
    if (searchData) {
      // Insight rows dedupe by mnum; legacy rows by slot_number (see lib/searchRowDedupe).
      const originalData = dedupeSearchRows(searchData);

      // Insight (insightInstance === 1, or rows tagged isInsightRow): keep raw
      // machines for grouped name/title/bank results. Non-insight unchanged.
      const hasInsightRows = originalData.some((r) =>
        isInsightCardRow(r as unknown as Record<string, unknown>)
      );
      if (isInsightCasino || hasInsightRows) {
        setInsightMachines(originalData);
        setFilterArr([]);
        setGameArr([]);
        setInsightSections([]);
        return;
      }

      setInsightMachines([]);
      setInsightSections([]);

      if (casinoId === casinoIdListEnum.RWC) {
        const resultGame = processDataOtherCasinosGame('game_name', originalData);
        const resultTheme = processDataOtherCasinos('theme', originalData);

        const formattedArrSortGameName = resultGame.sort((a, b) =>
          a.game_name.localeCompare(b.game_name)
        );

        const formattedsortedThemeData = resultTheme
          .sort((a, b) => a.game_name.localeCompare(b.game_name))
          .map((game) => ({
            ...game,
            sub_game_names: game.sub_game_names.sort((a, b) =>
              a.game_name.localeCompare(b.game_name)
            ),
          }));
        const formattedArr = [...formattedArrSortGameName, ...formattedsortedThemeData];
        const formattedArrSortGameNameArr = formattedArr.sort((a, b) =>
          a.game_name.localeCompare(b.game_name)
        );
        setFilterArr(formattedArrSortGameNameArr);
        setGameArr(formattedArrSortGameNameArr);
        return;
      }
      if (casinoId === casinoIdListEnum.SSC || casinoId === casinoIdListEnum.ACH) {
        const resultGame = processDataOtherCasinosGame('game_name', originalData);
        const resultTheme = processDataOtherCasinos('theme', originalData);

        const formattedArrSortGameName = [...resultGame]
          .map((g) => ({
            ...g,
            game_name: g.game_name.trim().replace(/\s+/g, ' ').toUpperCase(),
          }))
          .sort((a, b) => a.game_name.localeCompare(b.game_name));

        const formattedsortedThemeData = resultTheme
          .slice()
          .sort((a, b) => a.game_name.localeCompare(b.game_name))
          .map((game) => ({
            ...game,
            sub_game_names: [...game.sub_game_names].sort((a, b) =>
              a.game_name.localeCompare(b.game_name)
            ),
          }));
        const formattedArr = [...formattedArrSortGameName, ...formattedsortedThemeData];
        const formattedArrSortGameNameArr = formattedArr.sort((a, b) =>
          a.game_name.localeCompare(b.game_name)
        );
        setFilterArr(formattedArrSortGameNameArr);
        setGameArr(formattedArrSortGameNameArr);
        return;
      }

      const resultGameName = processData('game_name', originalData);
      const resultManufacturer = processData('manufacturer', originalData);
      const resultTheme = processData('theme', originalData);
      const resultProgressiveDesc = processData('progressive_type_desc', originalData);
      const resultStyleDesc = processData('style_desc', originalData);
      const resultSystemType = processData('type', originalData);
      const resultSystemCommonName = processData('common_name', originalData);

      const formattedArr = [
        ...resultGameName,
        ...resultProgressiveDesc,
        ...resultTheme,
        ...resultManufacturer,
        ...resultStyleDesc,
        ...resultSystemType,
        ...resultSystemCommonName,
      ];

      resultSystemCommonName.sort((a, b) =>
        a.sub_game_names.length > b.sub_game_names.length ? -1 : 1
      );

      const formattedArrSort = formattedArr.sort((a, b) =>
        a.sub_game_names.length > b.sub_game_names.length ? -1 : 1
      );

      setFilterArr(formattedArrSort);
      setGameArr(formattedArrSort);
    }
  }, [searchData, isInsightCasino, casinoId]);

  useEffect(() => {
    setInsightSectionOpen({});
    setInsightGroupOpen({});
  }, [searchValue]);

  useEffect(() => {
    let isIgnore = false;

    if (!isIgnore && (gameArr || insightMachines.length > 0)) {
      populateDropdown(searchValue);
    }

    return () => {
      isIgnore = true;
    };
  }, [searchValue, isInsightCasino, insightMachines]);

  const populateDropdown = (filter: string): void => {
    let matches = gameArr;

    // Insight: collapsible Floor / Manufacturer / Game Name sections.
    // Matching: manufacturer, name/title, mnum + slotNumber (Asset),
    // floorLocation (space-normalized prefix), gameType, progressive, 2-char bank.
    // Tapping a group selects those machines.
    if (isInsightCasino || insightMachines.length > 0) {
      setInsightSections(buildInsightSearchSections(insightMachines, filter));
      setGameArr([]);
      return;
    }
    setInsightSections([]);

    if (filter) {
      const filterText = filter?.toLowerCase();
      const isNumberSearch = /^\d+$/.test(filterText);
      if (isNumberSearch) {
        matches = filterArr
          .filter((item: GameInfo) => {
            const slotNumberMatch = item.sub_game_names.some(
              (subGame: SubGame) =>
                subGame.slot_number && subGame.slot_number.toString().includes(filterText)
            );
            const gameNameMatch = item.game_name?.toLowerCase().includes(filterText);
            const subGameNameMatch = item.sub_game_names.some((sub: SubGame) =>
              sub.game_name?.toLowerCase().includes(filterText)
            );
            return slotNumberMatch || gameNameMatch || subGameNameMatch;
          })
          .map((item: GameInfo) => {
            const filteredSubGameNames = item.sub_game_names.filter(
              (subGame: SubGame) =>
                (subGame.slot_number && subGame.slot_number.toString().includes(filterText)) ||
                (subGame.game_name && subGame.game_name.toLowerCase().includes(filterText))
            );
            return {
              ...item,
              other_sub_games: filteredSubGameNames,
              sub_game_names: filteredSubGameNames,
            };
          });
        matches = matches.sort((a: GameInfo, b: GameInfo) => {
          return a.game_name.localeCompare(b.game_name);
        });
        setGameArr(matches);
      } else {
        matches = filterArr
          .filter((item: GameInfo) => {
            const gameNameMatch = item.game_name?.toLowerCase().includes(filterText);
            const subGameNameMatch = item.sub_game_names.some((sub: SubGame) =>
              sub.game_name?.toLowerCase().includes(filterText)
            );
            return gameNameMatch || subGameNameMatch;
          })
          .map((item: GameInfo) => {
            const hasSlotNumber = item.sub_game_names.some(
              (sub: SubGame) =>
                sub.slot_number !== undefined &&
                sub.slot_number !== null &&
                isTextFinite(String(sub.slot_number))
            );
            return {
              ...item,
              other_sub_games: item.sub_game_names,
              sub_game_names: hasSlotNumber ? [] : item.sub_game_names,
            };
          });

        matches = matches.sort((a: GameInfo, b: GameInfo) => {
          return a.game_name.localeCompare(b.game_name);
        });

        setGameArr(matches);
      }
    } else {
      setGameArr(filterArr);
    }
  };

  const onSelectGroup = (itemArr: any) => {
    // Collect search slot rows (not bare slot_number). Insight rows use
    // slot_number = floorLocation for placement; SelectedSlots / map pins need
    // mnum via searchRowDetailId — same as HotListReport title/theme chips.
    const slotsArr = flattenInsightSearchSlotRows(itemArr)
      .map((row) => searchRowDetailId(row))
      .filter((n): n is number => n !== null)
      .map(String);

    setSearchValue('');
    // Bank section rows are labeled "Bank …"; mark as bank filter so the chip
    // and Select All path treat them like map bank picks. Titles/themes clear it.
    const rawName = String(itemArr.game_name ?? '');
    const displayName = rawName
      .replace(/^Floor Location\b/i, 'Bank Location')
      .replace(/^Floor\b/i, 'Bank');
    const isBankGroup = /^Bank\b/i.test(displayName);
    if (isBankGroup) {
      // Same apply path as Explore map bank-label taps.
      applyBankFilter(slotCommands, slotCompareCommands, displayName, slotsArr);
    } else {
      slotCommands.SetSearchMode(true);
      slotCommands.SelectBankFilter(false);
      slotCommands.SelectedSlotsName(displayName);
      slotCommands.SelectedSlots(slotsArr as string[]);
      slotCompareCommands.setSlotNumbers({
        slotNumbers: [],
      });
    }
    closeHandler();
  };

  /**
   * Open EGM Details with mnum (egmDetailId). Insight search rows store
   * floorLocation on slot_number — never pass that to Details/graph/trends.
   */
  const onSelectRow = (
    row: {
      mnum?: unknown;
      slot_number?: unknown;
      slots?: Array<{ mnum?: unknown; slot_number?: unknown }>;
      other_sub_games?: Array<{
        slots?: Array<{ mnum?: unknown; slot_number?: unknown }>;
        mnum?: unknown;
        slot_number?: unknown;
      }>;
      total?: number;
    } | null | undefined,
    gamename: string
  ) => {
    const id = searchSelectionDetailId(row) ?? searchRowDetailId(row);
    if (id === null) return;
    onSelect(id, gamename);
  };

  const onSelect = (slotnumber: number, gamename: string) => {
    setGameName(gamename);
    slotCommands.SetSearchMode(true);
    addToSlotHistory(slotnumber);
    onClickItem([slotnumber]);
  };

  const clearSearch = () => {
    setGameName(null);
    slotCommands.ClearSelectedSlots();
    slotCommands.SetSearchMode(false);
  };

  const handleClearSearchInput = (event: any) => {
    event.preventDefault();
    event.stopPropagation();

    setSearchValue('');
    clearSearch();
  };

  /**
   * Label tap on Floor / Asset / Manufacturer / Game Names.
   * Single-machine results (including Asset with name+title children) open
   * Details with mnum. Multi-machine groups become SelectedSlots filters.
   */
  const selectInsightGroupMachines = (eachgame: InsightSearchGroup | any) => {
    // Asset rows carry group.mnum; single-EGM groups resolve via nested slots.
    // Do this BEFORE treating other_sub_games.length > 1 as multi-select —
    // Asset name+title children are two rows for one machine, not two EGMs.
    const singleDetailId = searchSelectionDetailId(eachgame);
    if (singleDetailId !== null && (eachgame?.total === 1 || eachgame?.mnum != null)) {
      onSelect(singleDetailId, eachgame.game_name);
      return;
    }
    if (eachgame?.slot_number || eachgame?.mnum) {
      onSelectRow(eachgame, eachgame.game_name);
      return;
    }
    // Prefer whole-group select whenever the row represents more than one machine
    // (e.g. "IGT (300)" / "Bank I4 (8)").
    if (Array.isArray(eachgame.other_sub_games) && eachgame.total > 1) {
      onSelectGroup(eachgame);
      return;
    }
    if (Array.isArray(eachgame.other_sub_games) && eachgame.other_sub_games.length === 1) {
      const subGame = eachgame.other_sub_games[0];
      if (Array.isArray(subGame.slots) && subGame.slots.length === 1) {
        onSelectRow(subGame.slots[0], subGame.game_name);
      } else if (Array.isArray(subGame.slots) && subGame.slots.length > 1) {
        onSelectGroup(subGame);
      } else {
        onSelectRow(subGame, subGame.game_name);
      }
    } else if (Array.isArray(eachgame.other_sub_games) && eachgame.other_sub_games.length > 1) {
      // Multiple child rows but total machines unknown — filter by collected mnums.
      onSelectGroup(eachgame);
    }
  };

  const renderChildRows = (eachgame: InsightSearchGroup | any, key: string) => {
    if (!eachgame?.sub_game_names?.length) return null;
    return (
      <ul className="list-style">
        {eachgame.sub_game_names.map((gamedesc: any, index2: number) => (
          <li
            key={index2}
            className="each-item"
            id={'search-by-number' + key + '-' + index2.toString()}
            onClick={() => {
              if (gamedesc.slots) {
                if (gamedesc.slots.length === 1) {
                  onSelectRow(gamedesc.slots[0], gamedesc.game_name);
                } else {
                  onSelectGroup(gamedesc);
                }
              } else {
                onSelectRow(gamedesc, gamedesc.game_name);
              }
            }}
          >
            <div className="game-name">
              {gamedesc.slot_number && (
                <span className="slot-number">{gamedesc.slot_number} </span>
              )}
              {gamedesc.game_name && (
                <span className="slot-name">{gamedesc.game_name.toLowerCase()}</span>
              )}
              {gamedesc?.slots && (
                <span className="slot-count"> ({gamedesc?.slots.length})</span>
              )}
            </div>
          </li>
        ))}
      </ul>
    );
  };

  const renderInsightSearchGroup = (
    eachgame: InsightSearchGroup | any,
    key: string,
    options?: { collapsible?: boolean; childrenOnly?: boolean }
  ) => {
    if (options?.childrenOnly) {
      return (
        <div key={key} className="insight-search-group insight-search-group--flat">
          {renderChildRows(eachgame, key)}
        </div>
      );
    }

    const collapsible = Boolean(options?.collapsible);
    // Floor / Manufacturer rows are collapsed by default until the chevron is used.
    const isGroupOpen = collapsible ? Boolean(insightGroupOpen[key]) : true;
    const headerLabel =
      eachgame.preserveHeaderCasing ||
      String(eachgame.game_name).startsWith('Bank ') ||
      String(eachgame.game_name).startsWith('Bank Location') ||
      String(eachgame.game_name).startsWith('Asset ')
        ? eachgame.game_name
        : titleCase(eachgame.game_name);
    // Floor / Manufacturer always show machine count; Game Name keeps >1 only.
    const showCount = collapsible ? eachgame.total >= 1 : eachgame.total > 1;

    return (
      <div key={key} className="insight-search-group-wrap">
        <ul className="list-header">
          <li>
            <div id={'list-header' + key} className="insight-search-group">
              <div className="insight-search-group__row">
                <div
                  className="insight-search-group__label game-name"
                  onClick={() => selectInsightGroupMachines(eachgame)}
                >
                  {eachgame.slot_number && (
                    <span className="slot-number">{eachgame.slot_number} </span>
                  )}
                  {eachgame.game_name && <span className="slot-name">{headerLabel}</span>}
                  {showCount && <span className="slot-count"> ({eachgame.total})</span>}
                  <span className="denom">{eachgame.denom}</span>
                </div>
                {collapsible && (
                  <button
                    type="button"
                    className="insight-search-group__chevron-btn"
                    aria-label={isGroupOpen ? 'Collapse group' : 'Expand group'}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setInsightGroupOpen((prev) => ({
                        ...prev,
                        [key]: !isGroupOpen,
                      }));
                    }}
                  >
                    <IonIcon
                      icon={isGroupOpen ? chevronDown : chevronForward}
                      className="insight-search-group__chevron"
                    />
                  </button>
                )}
              </div>
              {isGroupOpen && renderChildRows(eachgame, key)}
            </div>
          </li>
        </ul>
      </div>
    );
  };

  return (
    <>
      <SmartLoading loading={searchLoading} />
      {!searchLoading && (
        <div className={`m-slot-search ${customCls}`}>
          {!gameName ? (
            <div className="content-container">
              <TextField
                id="search-input"
                label="Search"
                variant="outlined"
                fullWidth
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IonIcon icon={search} className="search-box-section-icon" />
                    </InputAdornment>
                  ),
                  endAdornment: searchValue && (
                    <InputAdornment
                      position="end"
                      onClick={handleClearSearchInput}
                      style={{ cursor: 'pointer' }}
                    >
                      <IonIcon icon={closeCircle} className="search-box-section-icon" />
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  autocomplete: autocomplete,
                }}
              />

              <div ref={gameInputRefDiv}>
                {searchValue &&
                  ((isInsightCasino || insightMachines.length > 0
                    ? insightSections.length > 0
                    : gameArr && gameArr?.length > 0) && (
                  <div className={`slots-list-container ${cssClass}`} id="slot-list-container">
                    {(isInsightCasino || insightMachines.length > 0) &&
                      insightSections.map((section) => {
                        const isOpen =
                          insightSectionOpen[section.id] !== undefined
                            ? insightSectionOpen[section.id]
                            : section.defaultOpen;
                        // Folded single Floor/Manufacturer group: header is "I 4 (8)" / "AGS (24)";
                        // games list directly under it (no nested group row).
                        const flatGroup = section.flat ? section.groups[0] : null;
                        return (
                          <div className="insight-search-section" key={section.id}>
                            <div className="insight-search-section__header-row">
                              <button
                                type="button"
                                className="insight-search-section__header"
                                onClick={() => {
                                  if (flatGroup) {
                                    selectInsightGroupMachines(flatGroup);
                                    return;
                                  }
                                  setInsightSectionOpen((prev) => ({
                                    ...prev,
                                    [section.id]: !isOpen,
                                  }));
                                }}
                              >
                                <span className="insight-search-section__title">
                                  {section.title} ({section.total})
                                </span>
                              </button>
                              <button
                                type="button"
                                className="insight-search-section__chevron-btn"
                                aria-label={isOpen ? 'Collapse section' : 'Expand section'}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setInsightSectionOpen((prev) => ({
                                    ...prev,
                                    [section.id]: !isOpen,
                                  }));
                                }}
                              >
                                <IonIcon
                                  icon={isOpen ? chevronDown : chevronForward}
                                  className="insight-search-section__chevron"
                                />
                              </button>
                            </div>
                            {isOpen &&
                              (flatGroup
                                ? renderInsightSearchGroup(flatGroup, `${section.id}-flat`, {
                                    collapsible: false,
                                    childrenOnly: true,
                                  })
                                : section.groups.map((eachgame, index) =>
                                    renderInsightSearchGroup(eachgame, `${section.id}-${index}`, {
                                      // Multi Floor/Manufacturer rows collapse by default.
                                      collapsible:
                                        section.id === 'floor' || section.id === 'manufacturer',
                                    })
                                  ))}
                          </div>
                        );
                      })}
                    {!(isInsightCasino || insightMachines.length > 0) &&
                      gameArr &&
                      gameArr.map((eachgame: any, index: number) =>
                        renderInsightSearchGroup(eachgame, String(index))
                      )}
                  </div>
                ))}
              </div>
              {gameName && (
                <div onClick={clearSearch}>
                  <IonIcon icon={close} style={btnstyles} className="search-box-section-close" />
                </div>
              )}
            </div>
          ) : (
            <div className="search-box-section">
              {/* <div className="search-box-section-text">
                {gameName?.game_name
                  ? titleCase(gameName?.game_name as string)
                  : titleCase(gameName as string)}
              </div>
              {gameName && (
                <div className="slot-close-icon" onClick={handleClearSearchInput}>
                  <IonIcon icon={closeOutline} className="" />
                </div>
              )} */}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default Search;
